import { isCancel } from 'axios';
import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { appConfig } from '../../helpers/app-config/app-config';
import type { MinifiedTask } from '../tasks-client/task-client.type';
import type {
  AvailableMeasuresResponse,
  BulkUpdateWorkPeriodsPayload,
  CustomSprintMeasureDataResponse,
  GetAvailableMeasuresPayload,
  GetCustomSprintMeasurePayload,
  GetTimeAllocationsPayload,
  GetWorkPeriodsMeasurePayload,
  GetWorkPeriodsPayload,
  MeasureDetail,
  TaskTypeBreakdownResponse,
  TimeAllocation,
  WorkPeriod,
  WorkPeriodGoal,
} from './work-periods-client.type';

const axiosInstance = AxiosInstance();
const baseURL = appConfig.VITE_APP_API_URL + '/api/work-periods/';
const useAsyncMeasure = appConfig.VITE_FEATURE_FLAG_USE_ASYNC_CALLS === 'true';

/**
 * Fetches a list of available measures for the given scope and entity.
 *
 * @param {GetAvailableMeasuresPayload} payload - The payload that is passed to the API call.
 * @param {ModuleType[]} payload.scope - The scope of the available measures.
 * @param {string} [payload.portfolio_id] - The ID of the portfolio.
 * @param {string} [payload.project_id] - The ID of the project.
 * @param {string} [payload.subproject_id] - The ID of the subproject.
 * @param {string} [payload.work_period_id] - The ID of the work period.
 * @param {string} [payload.organization_id] - The ID of the organization.
 * @return {Promise<AvailableMeasuresResponse>} - A promise that resolves to the list of available measures.
 */
const getAvailableMeasures = (
  { scope, portfolio_id, project_id, subproject_id, work_period_id, organization_id }: GetAvailableMeasuresPayload,
  signal?: AbortSignal,
): Promise<AvailableMeasuresResponse> => {
  const params = {
    scope: scope.join(','),
    portfolio_id,
    project_id,
    subproject_id,
    work_period_id,
    organization_id,
  };

  return axiosInstance
    .get(`${baseURL}available-measures/`, { params, signal })
    .then((response) => response.data)
    .catch((error) => {
      if (isCancel(error)) return Promise.reject(error);
      return Promise.reject(error?.response?.data || 'Error while fetching available measures');
    });
};

/**
 * Fetches time allocations based on the provided parameters.
 *
 * @param {GetTimeAllocationsPayload} params - The parameters for fetching time allocations.
 * @param {string} params.start_date - The start date for the time allocations.
 * @param {string} params.end_date - The end date for the time allocations.
 * @param {TimeAllocationType} params.time_allocation_type - The time allocation type.
 * @param {string} params.organization_id - The ID of the organization.
 * @return {Promise<TimeAllocation[]>} - A promise that resolves to an array of time allocations.
 */
const getTimeAllocations = (
  { start_date, end_date, time_allocation_type, organization_id }: GetTimeAllocationsPayload,
  signal?: AbortSignal,
): Promise<TimeAllocation[]> => {
  const params = {
    start_date,
    end_date,
    time_allocation_type,
    organization_id,
  };

  return axiosInstance
    .get(`${baseURL}time-allocations/`, { params, signal })
    .then((response) => response.data)
    .catch((error) => {
      if (isCancel(error)) return Promise.reject(error);
      return Promise.reject(error?.response?.data || 'Error while fetching time allocations');
    });
};

/**
 * Fetches an array of work periods based on the provided parameters.
 *
 * @param {GetWorkPeriodsPayload} params - The parameters for fetching work periods.
 * @param {string} params.subproject_id - The ID of the subproject.
 * @param {string} [params.parent] - The ID of the parent work period.
 * @param {string} [params.external_id] - The external ID of the work period.
 * @param {string} [params.time_span] - The time span for the work periods.
 * @param {string} [params.time_tense] - The time tense for the work periods.
 * @return {Promise<WorkPeriod[]>} - A promise that resolves to an array of work periods.
 */
const getWorkPeriods = ({
  subproject_id,
  parent,
  external_id,
  time_span,
  time_tense,
}: GetWorkPeriodsPayload): Promise<WorkPeriod[]> => {
  const params = { subproject_id, parent, external_id, time_span, time_tense };

  return axiosInstance
    .get(baseURL, { params })
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while fetching work periods'));
};

/**
 * Bulk updates work periods for a subproject
 *
 * @param {BulkUpdateWorkPeriodsPayload[]} work_periods - The work periods to update
 * @return {Promise<WorkPeriod[]>} A promise that resolves to the updated work periods
 */
const bulkUpdateWorkPeriods = (work_periods: BulkUpdateWorkPeriodsPayload[]): Promise<WorkPeriod[]> => {
  return axiosInstance
    .patch(`${baseURL}bulk_update/`, { work_periods })
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while bulk updating work periods'));
};

/**
 * Fetches a work period measure based on the provided parameters.
 *
 * @param {GetWorkPeriodsMeasurePayload} work_period_id - The ID of the work period.
 * @param {string} subproject_id - The ID of the subproject.
 * @param {string} project_id - The ID of the project.
 * @param {string} portfolio_id - The ID of the portfolio.
 * @param {string} initiative_id - The ID of the initiative.
 * @param {string} epic_id - The ID of the epic.
 * @param {string} page_id - The ID of the page.
 * @param {string} start_date - The start date for the measure.
 * @param {string} end_date - The end date for the measure.
 * @param {string} measure_name - The name of the measure.
 * @param {string} time_allocation_type - The time allocation type.
 * @param {string} transformer - The transformer for the measure.
 * @param {boolean} force_recalculation - Flag to force recalculation.
 * @param {AbortSignal} signal - The signal to abort the request.
 * @param {boolean} is_custom - Optional flag to indicate if the measure is custom.
 * @param {string} module_type - The module type.
 * @param {boolean} superuser_force_recalculation - Optional superuser force recalculation flag.
 * @param {boolean} use_groups - Optional flag to toggle between workflow groups and individual statuses.
 * @param {string} organization_id - The ID of the organization.
 * @return {Promise<T>} A promise that resolves to the fetched work period measure data.
 */
const getWorkPeriodsMeasure = <T>({
  work_period_id,
  subproject_id,
  project_id,
  portfolio_id,
  initiative_id,
  epic_id,
  page_id,
  start_date,
  end_date,
  measure_name,
  time_allocation_type,
  transformer,
  force_recalculation,
  signal,
  is_custom,
  module_type,
  superuser_force_recalculation,
  use_groups,
  organization_id,
  task_type,
}: GetWorkPeriodsMeasurePayload & {
  signal?: AbortSignal;
  superuser_force_recalculation?: boolean;
}): Promise<T> => {
  const params = {
    work_period_id,
    subproject_id,
    project_id,
    portfolio_id,
    initiative_id,
    page_id,
    start_date,
    end_date,
    measure_name: measure_name.join(','),
    time_allocation_type,
    transformer,
    force_recalculation: force_recalculation ?? superuser_force_recalculation ?? false,
    epic_id,
    module_type,
    use_groups,
    organization_id,
    task_type,
  };

  let url = '';
  if (useAsyncMeasure) {
    url = is_custom ? `${baseURL}async-custom-measure/` : `${baseURL}async-measure/`;
  } else {
    url = is_custom ? `${baseURL}custom-measure/` : `${baseURL}measure/`;
  }

  return axiosInstance
    .get(url, { params, signal })
    .then((response) => response.data)
    .catch((error) => {
      if (isCancel(error)) return Promise.reject(error);
      return Promise.reject(error?.response?.data || 'Error while fetching work periods measure');
    });
};

/**
 * Fetches a work period calculation tasks based on the provided parameters.
 *
 * @param {string} [payload.work_period_id] - The ID of the work period.
 * @param {string} payload.subproject_id - The ID of the subproject.
 * @param {string} [payload.project_id] - The ID of the project.
 * @param {string} [payload.portfolio_id] - The ID of the portfolio.
 * @param {string} [payload.initiative_id] - The ID of the initiative.
 * @param {string} [payload.epic_id] - The ID of the epic.
 * @param {string} [payload.page_id] - The ID of the page.
 * @param {string} payload.start_date - The start date for fetching tasks.
 * @param {string} payload.end_date - The end date for fetching tasks.
 * @param {string} payload.measure_name - The names of the measures to fetch tasks for.
 * @param {string} [payload.time_allocation_type] - The time allocation type.
 * @param {string} [payload.transformer] - The transformer for the measure.
 * @param {boolean} [payload.force_recalculation] - Flag to force recalculation.
 * @param {boolean | null} [payload.is_custom] - Flag to indicate if the measure is custom.
 * @param {string | null} [payload.module_type] - The module type.
 * @param {string} organization_id - The ID of the organization.
 * @return {Promise<MinifiedTask[]>} A promise that resolves to the fetched work period tasks data.
 * @throws {Error} If there's an error while fetching work period tasks.
 */

const getWorkPeriodsTasks = ({
  work_period_id,
  subproject_id,
  project_id,
  portfolio_id,
  initiative_id,
  epic_id,
  page_id,
  start_date,
  end_date,
  measure_name,
  time_allocation_type,
  transformer,
  force_recalculation,
  measure_filter,
  is_custom,
  module_type,
  use_groups,
  organization_id,
  task_type,
  signal,
}: GetWorkPeriodsMeasurePayload & { signal?: AbortSignal }): Promise<MinifiedTask[]> => {
  const params = {
    work_period_id,
    subproject_id,
    project_id,
    portfolio_id,
    initiative_id,
    epic_id,
    page_id,
    start_date,
    end_date,
    measure_name: measure_name.join(','),
    time_allocation_type,
    transformer,
    force_recalculation,
    measure_filter,
    module_type,
    use_groups,
    organization_id,
    task_type,
  };

  const url = is_custom ? `${baseURL}custom-tasks/` : `${baseURL}tasks/`;

  return axiosInstance
    .get(url, { params, signal })
    .then((response) => response.data)
    .catch((error) => {
      if (isCancel(error)) return Promise.reject(error);
      return Promise.reject(error?.response?.data || 'Error while fetching work periods tasks');
    });
};

/**
 * Fetches a custom sprint measure based on the provided parameters.
 *
 * @param {GetCustomSprintMeasurePayload} params - The parameters for fetching the custom sprint measure.
 * @return {Promise<CustomSprintMeasureDataResponse>} A promise that resolves to the fetched custom sprint measure data.
 */
const getCustomSprintMeasure = (params: GetCustomSprintMeasurePayload): Promise<CustomSprintMeasureDataResponse> => {
  const url = `${baseURL}custom-sprint-measure/`;
  return axiosInstance
    .get(url, { params })
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while fetching custom sprint measure'));
};

/**
 * Fetches detailed information about a specific measure.
 *
 * @param {string} measureName - The name of the measure to get details for
 * @param {string} [organizationId] - The ID of the organization to filter by
 * @return {Promise<MeasureDetail>} A promise that resolves to the measure details
 */
const getMeasureDetail = (measureName: string, organizationId?: string): Promise<MeasureDetail> => {
  const params = organizationId ? { organization_id: organizationId } : {};

  return axiosInstance
    .get(`${baseURL}measure/${measureName}/`, { params })
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while fetching measure details'));
};

/**
 * Fetches the goals for a specific work period.
 *
 * @param {string} workPeriodId - The ID of the work period.
 * @return {Promise<WorkPeriodGoal[]>} A promise that resolves to the goals for the work period.
 */
function getWorkPeriodGoals(workPeriodId: string, signal?: AbortSignal): Promise<WorkPeriodGoal[]> {
  return axiosInstance
    .get(`${baseURL}${workPeriodId}/goals/`, { signal })
    .then((response) => response.data)
    .catch((error) => {
      if (isCancel(error)) return Promise.reject(error);
      return Promise.reject(error?.response?.data || 'Error while fetching work period goals');
    });
}

/**
 * Fetches the goal(s) for a specific work period from the source system.
 *
 * @param {string} workPeriodId - The ID of the work period.
 * @return {Promise<{external_goal: string}>} A promise that resolves to the goal(s) text from the source system.
 */
function getWorkPeriodExternalGoal(workPeriodId: string, signal?: AbortSignal): Promise<{ external_goal: string }> {
  return axiosInstance
    .get(`${baseURL}${workPeriodId}/external-goal/`, { signal })
    .then((response) => response.data)
    .catch((error) => {
      if (isCancel(error)) return Promise.reject(error);
      return Promise.reject(error?.response?.data || 'Error while fetching work period goals');
    });
}

/**
 * Updates a work period goal.
 *
 * @param {WorkPeriodGoal} goal - The goal to update.
 * @return {Promise<WorkPeriodGoal>} A promise that resolves to the updated goal.
 */
function updateWorkPeriodGoal(goal: WorkPeriodGoal): Promise<WorkPeriodGoal> {
  return axiosInstance
    .put(`${baseURL}${goal.work_period}/goals/${goal.id}/`, goal)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while updating work period goal'));
}

/**
 * Fetches task type breakdown data for a specific work period.
 *
 * @param {string} work_period_id - The ID of the work period.
 * @param {string} organizationId - The ID of the organization.
 * @return {Promise<TaskTypeBreakdownResponse>} A promise that resolves to the task type breakdown data.
 */
const getWorkPeriodTaskTypeBreakdown = (
  work_period_id: string,
  organizationId?: string,
  signal?: AbortSignal,
): Promise<TaskTypeBreakdownResponse> => {
  const params = {
    work_period_id,
    organization_id: organizationId,
  };
  return axiosInstance
    .get(`${baseURL}task_type_breakdown/`, { params, signal })
    .then((response) => response.data)
    .catch((error) => {
      if (isCancel(error)) return Promise.reject(error);
      return Promise.reject(error?.response?.data || 'Error while fetching task type breakdown');
    });
};

/**
 * Creates a work period goal.
 *
 * @param {Omit<WorkPeriodGoal, 'id'>} goal - The goal to create.
 * @return {Promise<WorkPeriodGoal>} A promise that resolves to the created goal.
 */
function createWorkPeriodGoal(goal: Omit<WorkPeriodGoal, 'id'>): Promise<WorkPeriodGoal> {
  return axiosInstance
    .post(`${baseURL}${goal.work_period}/goals/`, goal)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while creating work period goal'));
}

/**
 * Deletes a work period goal.
 *
 * @param {WorkPeriodGoal} goal - The goal to delete.
 * @return {Promise<WorkPeriodGoal>} A promise that resolves to the deleted goal.
 */
function deleteWorkPeriodGoal(goal: WorkPeriodGoal): Promise<WorkPeriodGoal> {
  return axiosInstance
    .delete(`${baseURL}${goal.work_period}/goals/${goal.id}/`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while deleting work period goal'));
}

export {
  bulkUpdateWorkPeriods,
  createWorkPeriodGoal,
  deleteWorkPeriodGoal,
  getAvailableMeasures,
  getCustomSprintMeasure,
  getMeasureDetail,
  getTimeAllocations,
  getWorkPeriodExternalGoal,
  getWorkPeriodGoals,
  getWorkPeriods,
  getWorkPeriodsMeasure,
  getWorkPeriodsTasks,
  getWorkPeriodTaskTypeBreakdown,
  updateWorkPeriodGoal,
};
